import type { JobDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import type { PaymentInformationDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/payment';
import type { OnlineActivityDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/activity';
import type { DSSPaymentFrequency } from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { BankAccountFieldProps } from '../../../recoil/types/types';
import {
    Box,
    GridItem,
    RadioField,
    RadioInput,
    validateAccountNumber,
    validateBankNumber,
    validateCpr
} from '@flos/react-ui';
import { useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { usePaymentInformationHook } from '../../../hooks/usePaymentInformation';
import { jobAtom } from '../../../recoil/atoms/jobAtom';
import { fetchedPaymentInformationAtom } from '../../../recoil/atoms/fetchedPaymentInformationAtom';
import { updatedPaymentInformationAtom } from '../../../recoil/atoms/updatedPaymentInformationAtom';
import { bankAccountValidityStatusAtom } from '../../../recoil/atoms/bankAccountValidityStatusAtom';
import { hasCoAccountHolderAtom } from '../../../recoil/atoms/hasCoAccountHolderAtom';
import { paymentFrequencyMap } from '../../../utils/currencyUtil';
import { useCreateActivityHook } from '../../../hooks/useCreateActivity';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import { convertDateToLocalDateDTO } from '../../../utils/dateUtil';
import { useHippoText } from '../../../hooks/useHippoText';
import { track } from '../../../utils/trackingUtil';
import AccordionWizardTemplate from '../components/AccordionWizardTemplate';
import StringParagraph from '../components/StringParagraph';
import PaymentInformationErrorModal from '../components/PaymentInformationErrorModal';
import BankAccountFields from './BankAccountFields';

const PaymentInformationStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const { updatePaymentInformation } = usePaymentInformationHook();
    const { createActivity } = useCreateActivityHook();
    const [jobData, setJobData] = useRecoilState(jobAtom);
    const [paymentInformation, setPaymentInformation] = useRecoilState(
        fetchedPaymentInformationAtom
    );
    const [paymentInfo, setPaymentInfo] = useState<BankAccountFieldProps>({
        accountNumber: paymentInformation.accountNumber ?? '',
        bankReference: paymentInformation.bankReference ?? ''
    });
    const accountHolder = useRecoilValue(accountHolderAtom);
    const updatedPaymentInfo = useRecoilValue(updatedPaymentInformationAtom);
    const isBankAccountValid = useRecoilValue(bankAccountValidityStatusAtom);
    const hasCoAccountHolder = useRecoilValue(hasCoAccountHolderAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [showInformationErrorModal, setShowInformationErrorModal] = useState(false);
    const [isFirstPaymentError, setIsFirstPaymentError] = useState(true);
    const { selectedPaymentFrequency = paymentFrequencyMap.monthly } = jobData;
    const {
        paymentMethod = 'BS_Top',
        yearlyAllowedPaymentMethods = [],
        monthlyAllowedPaymentMethods = []
    } = paymentInformation;
    const { cpr } = updatedPaymentInfo;
    const isSelectedPaymentFrequencyOneTime =
        selectedPaymentFrequency === paymentFrequencyMap.onetime;
    const isSelectedPaymentFrequencyMonthly =
        selectedPaymentFrequency === paymentFrequencyMap.monthly;
    const paymentPlan = isSelectedPaymentFrequencyMonthly
        ? monthlyAllowedPaymentMethods
        : yearlyAllowedPaymentMethods;
    const shouldRenderFIKOption =
        !isSelectedPaymentFrequencyMonthly && paymentPlan.includes('FIK_Top');
    const shouldRenderBSOption = paymentPlan.includes('BS_Top');
    const getHippoText = useHippoText();

    const hippoText = {
        paymentFrequencyDescription: getHippoText('DSS-PAYMENT-INFO-SCREEN-FREQUENCY-HEADER'),
        monthlyOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-MONTHLY'),
        annualOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-YEARLY'),
        oneTimeOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-ONE-TIME'),
        methodDescription: getHippoText('DSS-PAYMENT-INFO-SCREEN-METHOD-HEADER'),
        bsOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-BS-HEADER'),
        monthlyFee: getHippoText('DSS-PAYMENT-INFO-SCREEN-MONTHLY-FEE-TOOLTIP'),
        annualFee: getHippoText('DSS-PAYMENT-INFO-SCREEN-YEARLY-FEE-TOOLTIP'),
        fikOption: getHippoText('DSS-PAYMENT-INFO-SCREEN-FIK-HEADER'),
        fikFee: getHippoText('DSS-PAYMENT-INFO-SCREEN-FEE-TOOLTIP')
    };

    const getCollectionFeeText = (): string => {
        return isSelectedPaymentFrequencyMonthly ? hippoText.monthlyFee : hippoText.annualFee;
    };

    const isFormValid =
        !!selectedPaymentFrequency && !!paymentMethod && paymentMethod === 'BS_Top'
            ? validateBankNumber(paymentInfo.bankReference) &&
              validateAccountNumber(paymentInfo.accountNumber) &&
              (hasCoAccountHolder ? validateCpr(cpr?.replace(/-/g, '')) : true) &&
              isBankAccountValid
            : true;

    const onNextClick = (): void => {
        setIsLoading(true);
        if (paymentInfo.accountNumber !== '' && paymentInfo.bankReference !== '') {
            setPaymentInformation((prev) => {
                return {
                    ...prev,
                    accountNumber: paymentInfo.accountNumber,
                    bankReference: paymentInfo.bankReference
                };
            });
        }
        void updatePaymentInformation(paymentInfo, true)
            .then(() => {
                track('Payment information step', [
                    { paymentMethod },
                    { paymentFrequency: selectedPaymentFrequency }
                ]);
                goNext();
            })
            .catch(async () => {
                if (!isFirstPaymentError) {
                    const onlineActivity: OnlineActivityDTO = {
                        activityCode: 'dss_unexpected_error_phone_req',
                        phoneNumber: accountHolder.phoneNumber,
                        targetDate: convertDateToLocalDateDTO(new Date())
                    };
                    await createActivity(onlineActivity);
                }
                setIsLoading(false);
                setShowInformationErrorModal(true);
            });
    };

    const closeModal = (): void => {
        setShowInformationErrorModal(false);
        setIsFirstPaymentError(false);
    };

    const changePaymentMethod = (value: string): void => {
        setPaymentInformation((prevData: PaymentInformationDTO) => ({
            ...prevData,
            paymentMethod: value
        }));
    };

    const onPaymentMethodChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        changePaymentMethod(event.target.value);
    };

    const onPaymentFrequencyChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        if (!paymentPlan.includes(value)) {
            changePaymentMethod('BS_Top');
        }
        setJobData((prevData: JobDTO) => ({
            ...prevData,
            selectedPaymentFrequency: value as DSSPaymentFrequency
        }));
    };

    return (
        <AccordionWizardTemplate
            goNext={onNextClick}
            disableNext={!isFormValid}
            isNextBtnLoading={isLoading}
        >
            <Box>
                <GridItem span={12}>
                    <RadioField
                        description={
                            <span className="h6">{hippoText.paymentFrequencyDescription}</span>
                        }
                        value={selectedPaymentFrequency}
                        onChange={onPaymentFrequencyChange}
                        required
                    >
                        {!isSelectedPaymentFrequencyOneTime && (
                            <RadioInput
                                value={paymentFrequencyMap.monthly}
                                label={hippoText.monthlyOption}
                            />
                        )}
                        {!isSelectedPaymentFrequencyOneTime && (
                            <RadioInput
                                value={paymentFrequencyMap.annual}
                                label={hippoText.annualOption}
                            />
                        )}
                        {isSelectedPaymentFrequencyOneTime && (
                            <RadioInput
                                value={paymentFrequencyMap.onetime}
                                label={hippoText.oneTimeOption}
                            />
                        )}
                    </RadioField>
                </GridItem>
                <GridItem className="u-spacing-stack-top-4" span={12}>
                    <RadioField
                        description={<span className="h6">{hippoText.methodDescription}</span>}
                        value={paymentMethod}
                        onChange={onPaymentMethodChange}
                        required
                    >
                        {shouldRenderBSOption && (
                            <RadioInput value="BS_Top" label={hippoText.bsOption} />
                        )}
                        {shouldRenderBSOption && (
                            <StringParagraph small>{getCollectionFeeText()}</StringParagraph>
                        )}
                        {shouldRenderFIKOption && (
                            <RadioInput value="FIK_Top" label={hippoText.fikOption} />
                        )}
                        {shouldRenderFIKOption && (
                            <StringParagraph small>{hippoText.fikFee}</StringParagraph>
                        )}
                    </RadioField>
                </GridItem>
                {paymentMethod === 'BS_Top' && shouldRenderBSOption && (
                    <BankAccountFields paymentInfo={paymentInfo} setPaymentInfo={setPaymentInfo} />
                )}
                {showInformationErrorModal && (
                    <PaymentInformationErrorModal
                        showModal={showInformationErrorModal}
                        closeModal={closeModal}
                        isFirstPaymentError={isFirstPaymentError}
                    />
                )}
            </Box>
        </AccordionWizardTemplate>
    );
};

export default PaymentInformationStep;
